import clsx from 'clsx';
import { ChangeEvent, FocusEvent, forwardRef, useState } from 'react';
import FormHelperText from '../FormHelperText';
import WarningIcon from '../Icon/WarningIcon';
import { generateAutocompleteValue } from './utils';
import styles from './TextField.module.css';
import { TextFieldProps, BorderColor } from './types';

const mapBorderColor: { [key in BorderColor]: string } = {
  inherit: styles.borderColor_inherit,
  primary: styles.borderColor_primary,
  secondary: styles.borderColor_secondary,
};

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      id,
      name,
      label,
      color = 'inherit',
      type = 'text',
      size = 'medium',
      variant = 'rounded',
      required,
      fullWidth,
      helperText,
      className,
      inputClassName,
      endAdornment,
      labelClassName,
      inputClasses,
      disabled,
      autoComplete = 'off',
      disableAutoComplete,
      error,
      inputProps,
      whiteSpace,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [autoCompleteValue, setAutoCompleteValue] = useState<any>(() => {
      if (!disableAutoComplete) return autoComplete;
      if (disableAutoComplete) {
        if (typeof id !== 'undefined' || typeof name !== 'undefined') {
          // TODO: +10 kbs to first load js
          return Buffer.from(id || name)
            .toString('base64')
            .slice(0, 5);
        } else {
          return generateAutocompleteValue();
        }
      }
    });

    return (
      <div
        className={clsx(
          styles.textFieldTopContainer,
          {
            [styles.textFieldTopContainerDisabled]: disabled,
          },
          className,
        )}
      >
        {label && (
          <label
            className={clsx(
              styles.textFieldLabel,
              styles.textFieldTransition,
              labelClassName,
              {
                ['labelError']: error,
              },
              'fieldLabel',
            )}
            htmlFor={name}
          >
            {required ? `${label}*` : label}
          </label>
        )}
        <div
          className={clsx(
            styles.inputContainer,
            inputClasses,
            {
              roundedInput: variant === 'rounded',
              [styles.square]: variant === 'square',
              [styles.focused]: isFocused,
            },
            'inputContainer',
          )}
        >
          <input
            required={required}
            id={id}
            ref={ref}
            name={name}
            type={type}
            aria-label={label || name}
            autoComplete={autoCompleteValue.toString()}
            className={clsx(
              styles.textField,
              inputClassName,
              {
                [styles.smallInput]: size === 'small',
                [styles.mediumInput]: size === 'medium',
              },

              mapBorderColor[color],
              { 'w-full': fullWidth },
              {
                [styles.borderColor_primary]: error,
              },
            )}
            disabled={disabled}
            style={{ whiteSpace: whiteSpace ? 'pre-wrap' : 'normal' }}
            {...inputProps}
            {...props}
            onFocus={(e: ChangeEvent<HTMLInputElement>) => {
              setIsFocused(true);
              props?.onFocus?.(e);
            }}
            onBlur={(e: FocusEvent<HTMLInputElement>) => {
              setIsFocused(false);
              props?.onBlur?.(e);
            }}
            onMouseOver={disableAutoComplete ? () => setAutoCompleteValue(generateAutocompleteValue()) : () => null}
          />
          {endAdornment || ''}
        </div>

        {error && (
          <FormHelperText data-testid="input-helper-text" className="flex items-center" error>
            <div className="flex">
              <WarningIcon disabled={disabled} />
            </div>
            {helperText}
          </FormHelperText>
        )}
      </div>
    );
  },
);

export default TextField;
